import { router } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Button } from "@/components/ui/Button"
import { Card } from "@/components/ui/Card"
import { Checkbox } from "@/components/ui/Checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { admin_claude_questions_path } from "@/lib/routes"
import QuestionBlock from "./components/QuestionBlock"
import { groupByQuestion, type ToolCall } from "./types"

type Filters = { person: string | null; problems: boolean }

type Props = {
  calls: ToolCall[]
  has_more: boolean
  limit: number
  people: { id: string; name: string }[]
  filters: Filters
}

const PAGE_SIZE = 200

// What people asked Claude about Atlas, and which tools Claude called for it.
export default function ClaudeQuestionsIndex({ calls, has_more, limit, people, filters }: Props) {
  const groups = groupByQuestion(calls)

  const visit = (next: Filters, nextLimit?: number) => {
    const params: Record<string, string | number> = {}
    if (next.person) params.person = next.person
    if (next.problems) params.problems = "1"
    if (nextLimit) params.limit = nextLimit
    router.get(admin_claude_questions_path(), params, { preserveState: true, preserveScroll: Boolean(nextLimit) })
  }

  return (
    <AppLayout header={<PageTitle title="Claude questions" subtitle="What people asked Claude about Atlas, and the tools Claude called to answer" />}>
      <div className="max-w-6xl space-y-3">
        <Card className="flex flex-wrap items-center gap-4 px-4 py-2.5">
          <Select value={filters.person ?? "all"} onValueChange={(v) => visit({ ...filters, person: v === "all" ? null : v })}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Everyone" />
            </SelectTrigger>
            <SelectContent align="start">
              <SelectItem value="all">Everyone</SelectItem>
              {people.map((person) => (
                <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={filters.problems} onChange={(e) => visit({ ...filters, problems: e.target.checked })} />
            Only calls that failed or found nothing
          </label>
          <span className="ml-auto text-sm">{groups.length} questions, {calls.length} tool calls</span>
        </Card>

        <Card className="divide-y divide-border overflow-hidden">
          {groups.length === 0 ? (
            <p className="px-4 py-6 text-sm">No questions yet.</p>
          ) : (
            groups.map((group) => <QuestionBlock key={group.key} group={group} />)
          )}
        </Card>

        {has_more && (
          <Button variant="outline" size="sm" onClick={() => visit(filters, limit + PAGE_SIZE)}>
            Load more
          </Button>
        )}
      </div>
    </AppLayout>
  )
}
